import moment from 'moment'
import * as XLSX from 'xlsx'
import { concatClientAndProjectName } from '../types/Engagement'
import { AllGuildTypes, Guild } from '../types/Guilds'
import { UserTeam } from '../types/UserTeam'
import {
  isEngagementActiveInRange,
  isTeamActiveInRange,
  isUserActiveOnDay,
} from './general'
import { engagementsDefaultValues } from './tribeOrgDefaultValues'

export interface TribeOrgExportRow {
  Tribe: string
  Team: string
  Engagement: string
  'SOW Start Date': string
  'SOW End Date': string
  Guild: string
  Name: string
  Allocation: string
  Label: string
}

interface TribeOrgExportTeam {
  id?: number
  name: string
  startDate?: string | Date
  endDate?: string | Date | null
  engagements?: Array<{
    projectName?: string
    startDate?: string | Date
    endDate?: string | Date | null
    client?: { name?: string }
  }>
  teamAllocations?: UserTeam[]
}

interface TribeOrgExportTribe {
  name: string
  teams?: TribeOrgExportTeam[]
}

interface MapTribesOrgOptions {
  guilds: Guild[]
  isSnapshot: boolean
  startDate?: string
  endDate?: string
}

const GUILD_SORT_ORDER = [
  AllGuildTypes.ADM,
  AllGuildTypes.BA,
  AllGuildTypes.SM,
  AllGuildTypes.Developer,
  AllGuildTypes.QA,
  AllGuildTypes.Design,
  AllGuildTypes.DevOps,
] as const

const formatSowDate = (date?: string | Date | null): string => {
  if (!date || date === 'N/A') return 'N/A'
  const parsed = moment(date)
  if (!parsed.isValid()) return 'N/A'
  return parsed.format('MM/DD/YYYY')
}

const formatAllocation = (allocation?: number | null): string => {
  const value = Number.isFinite(allocation) ? Math.round(Number(allocation)) : 0
  return `${value}%`
}

const isActiveAllocation = (
  allocation: UserTeam,
  isSnapshot: boolean,
): boolean => isUserActiveOnDay(allocation.user?.end_on, isSnapshot)

const resolveEngagement = (
  team: TribeOrgExportTeam,
  startDate?: string,
  endDate?: string,
) => {
  const engagements = (team.engagements ?? []).filter(engagement =>
    isEngagementActiveInRange(engagement, startDate, endDate),
  )
  if (engagements.length === 0) {
    return {
      name: engagementsDefaultValues.projectName,
      startDate: engagementsDefaultValues.startDate,
      endDate: engagementsDefaultValues.endDate,
    }
  }

  const engagement = engagements[0]
  return {
    name: concatClientAndProjectName(
      engagement.client?.name || '',
      engagement.projectName || '',
    ),
    startDate: engagement.startDate,
    endDate: engagement.endDate,
  }
}

const getGuildName = (guildId: number | undefined, guilds: Guild[]): string => {
  if (guildId === undefined || guildId === null) return ''
  return guilds.find(guild => guild.id === guildId)?.role ?? ''
}

const getGuildSortIndex = (guildId: number | undefined, guilds: Guild[]) => {
  const guildType = guilds.find(guild => guild.id === guildId)?.type
  const index = GUILD_SORT_ORDER.findIndex(type => type === guildType)
  return index === -1 ? GUILD_SORT_ORDER.length : index
}

const sortAllocations = (allocations: UserTeam[], guilds: Guild[]) => {
  return [...allocations].sort((a, b) => {
    const guildDiff =
      getGuildSortIndex(a.user?.guildId, guilds) -
      getGuildSortIndex(b.user?.guildId, guilds)
    if (guildDiff !== 0) return guildDiff

    const nameA = `${a.user?.firstName ?? ''} ${a.user?.lastName ?? ''}`.trim()
    const nameB = `${b.user?.firstName ?? ''} ${b.user?.lastName ?? ''}`.trim()
    return nameA.localeCompare(nameB)
  })
}

export const mapTribesOrgToExportRows = (
  tribes: TribeOrgExportTribe[],
  { guilds, isSnapshot, startDate, endDate }: MapTribesOrgOptions,
): TribeOrgExportRow[] => {
  const rows: TribeOrgExportRow[] = []

  tribes.forEach(tribe => {
    const teams = [...(tribe.teams ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name),
    )

    teams.forEach(team => {
      if (!isTeamActiveInRange(team, startDate, endDate)) return

      const engagement = resolveEngagement(team, startDate, endDate)

      const allocations = sortAllocations(
        (team.teamAllocations ?? []).filter(allocation =>
          isActiveAllocation(allocation, isSnapshot),
        ),
        guilds,
      )

      allocations.forEach(allocation => {
        const user = allocation.user
        if (!user || user.id === -1 || user.firstName === '---') return

        rows.push({
          Tribe: tribe.name,
          Team: team.name,
          Engagement: engagement.name,
          'SOW Start Date': formatSowDate(engagement.startDate),
          'SOW End Date': formatSowDate(engagement.endDate),
          Guild: getGuildName(user.guildId, guilds),
          Name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
          Allocation: formatAllocation(allocation.allocation),
          Label: allocation.role || '',
        })
      })
    })
  })

  return rows
}

export const getTribeOrgExportFilename = (
  selectedSnapshotLabel?: string,
): string => {
  if (selectedSnapshotLabel) {
    return `Tribe Org - ${selectedSnapshotLabel}.xlsx`
  }
  return `Tribe Org - ${moment().format('MMM D, YYYY')}.xlsx`
}

export const downloadTribeOrgReport = (
  tribes: TribeOrgExportTribe[],
  options: MapTribesOrgOptions & { selectedSnapshotLabel?: string },
) => {
  const rows = mapTribesOrgToExportRows(tribes, options)
  const wb = XLSX.utils.book_new()
  const ws = XLSX.utils.json_to_sheet(rows)
  XLSX.utils.book_append_sheet(wb, ws, 'Tribe Org')
  XLSX.writeFile(wb, getTribeOrgExportFilename(options.selectedSnapshotLabel))
}
